import PropTypes from "prop-types";
import React, { useState } from "react";

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  makeStyles,
  useTheme,
} from "@material-ui/core";
import { ExpandMore } from "@material-ui/icons";

import Typography from "../../../components/Helpers/Typography";
import useMediaQuery from "../../../hooks/useMediaQuery";
import { DateService } from "../../../services/dates.service";
import { TimelineStatus } from "../../../services/timeline.service";
import Badges from "./subcomponents/Badges";
import TimelineAccordionDetails from "./subcomponents/TimelineAccordionDetails";
import TimelineStatusHeaderRow from "./subcomponents/TimelineStatusHeaderRow";
import TimelineStatusSummary from "./subcomponents/TimelineStatusSummary";
import { VerticalLine } from "./subcomponents/VerticalLine";
import { vehicleHistoryStyles } from "./subcomponents/sharedStyles";

const useStyles = makeStyles(vehicleHistoryStyles);

const TimelineDateSection = ({
  date,
  timelineStatusesArray,
  handleClick,
  visibleEventTimestamp,
  mostRecent,
  onShowPathChange,
  visibleEvents,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const classes = useStyles();

  const [expandedItems, setExpandedItems] = useState([]);
  const handleExpansionChange = (timestamp, events) => {
    const alreadyExpanded = expandedItems.includes(timestamp);
    if (alreadyExpanded) {
      setExpandedItems(expandedItems.filter((timeString) => timeString !== timestamp));
      if (shouldHideEvents(events[0])) {
        onShowPathChange([]);
      }
    } else {
      setExpandedItems([...expandedItems, timestamp]);
    }
  };

  const handleShowPath = (events) => {
    if (shouldHideEvents(events[0])) {
      onShowPathChange([]);
    } else {
      onShowPathChange(events);
    }
  };

  const shouldHideEvents = (latestEvent) =>
    visibleEvents.length && visibleEvents[0].timestamp === latestEvent.timestamp;

  const expansionColor = (expanded) => (expanded ? "inherit" : theme.palette.text.secondary);

  const stateColor = (state, forceOpaque) => {
    if (!theme.palette.statusColors) {
      return "#000";
    }
    switch (state) {
      case "available":
        return theme.palette.statusColors.available;

      case "reserved":
      case "trip":
      case "hold":
        return theme.palette.statusColors.reserved;

      case "unavailable":
      case "out_of_service":
        return theme.palette.statusColors.unavailable;

      case "offline":
        return theme.palette.statusColors.offline;

      case "firmware_update":
      case "operator":
      case "charging":
        return theme.palette.statusColors.firmware_update;

      default:
        return forceOpaque ? "#FFF" : "#000";
    }
  };

  return (
    <div
      key={`date-${date}`}
      className="timeline-trip-row"
      style={{ background: theme.palette.background.dark }}
    >
      <div className={classes.date}>
        <Typography variant="caption" color="secondary">
          {DateService.format(parseInt(date, 10), "MM dd")}
        </Typography>
      </div>
      <div className={classes.root}>
        {timelineStatusesArray.map((timelineStatus, index) => {
          const events = timelineStatus.getTimelineEvents();
          const expanded = expandedItems.includes(timelineStatus.timestamp);
          return (
            <div
              key={timelineStatus.key}
              style={{
                position: "relative",
              }}
            >
              <Accordion
                disabled={!events.length}
                className={classes.Accordion}
                expanded={expanded}
                onChange={() => handleExpansionChange(timelineStatus.timestamp, events)}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  IconButtonProps={{
                    style: {
                      color: events.length ? expansionColor(expanded) : "rgba(0,0,0,0)",
                    },
                    className: classes.iconButton,
                  }}
                  style={{
                    opacity: 1,
                    background: expanded
                      ? theme.palette.background.selected
                      : theme.palette.background.timeline,
                    position: "relative",
                    borderLeft: `8px solid ${stateColor(timelineStatus.state, false)}`,
                  }}
                >
                  <TimelineStatusHeaderRow
                    timelineStatus={timelineStatus}
                    isMobile={isMobile}
                    isCurrentStatus={mostRecent && index === 0}
                  />
                  <Badges events={events} />
                </AccordionSummary>
                {expanded && (
                  <AccordionDetails className={classes.AccordionContents}>
                    <TimelineStatusSummary
                      events={events}
                      timelineStatus={timelineStatus.state}
                      onShowPathChange={() => handleShowPath(events)}
                    />
                    {events
                      .filter((event) => !event.diagnostic_only)
                      .map((event) => (
                        <TimelineAccordionDetails
                          event={event}
                          handleClick={handleClick}
                          isVisible={event.timestamp === visibleEventTimestamp}
                          timelineStatus={timelineStatus}
                          key={event.key}
                        />
                      ))}
                    <VerticalLine color={stateColor(timelineStatus.state, true)} />
                  </AccordionDetails>
                )}
              </Accordion>
            </div>
          );
        })}
      </div>
    </div>
  );
};

TimelineDateSection.propTypes = {
  mostRecent: PropTypes.bool.isRequired,
  date: PropTypes.string.isRequired,
  timelineStatusesArray: PropTypes.arrayOf(PropTypes.instanceOf(TimelineStatus)).isRequired,
  handleClick: PropTypes.func.isRequired,
  visibleEventTimestamp: PropTypes.string,
  onShowPathChange: PropTypes.func.isRequired,
  visibleEvents: PropTypes.array,
};

TimelineDateSection.defaultProps = {
  visibleEventTimestamp: "",
  visibleEvents: [],
};

export default TimelineDateSection;
